import React, { useMemo, useEffect } from 'react'
import {
  ReactFlow,
  Background,
  Controls,
  ReactFlowProvider,
  useReactFlow
} from '@xyflow/react'
import '@xyflow/react/dist/style.css'
import { groupBy } from 'lodash'
import { AssignedBadge } from 'shared/badges'
import { useBusinessRules } from 'api/hooks/use-business-rules'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faPaperPlane, faCheckCircle } from '@fortawesome/free-solid-svg-icons'

function FlowchartTab({ rule }) {
  const params = {
    process_type_id: rule.workflow_process_type_id,
    perPage: 1000,
    orderBy: 'step_number',
    orderDirection: 'asc'
  }

  if (rule.department) {
    params.department_code = rule.department
  }

  const { data, isSuccess } = useBusinessRules({
    params
  })

  const { nodes, edges } = useMemo(() => {
    if (!isSuccess) return { nodes: [], edges: [] }

    const rules = data.data.filter((r) => r.step_number !== null)
    const grouped = groupBy(rules, 'step_number')
    const steps = Object.keys(grouped)
      .map((n) => parseInt(n, 10))
      .sort((a, b) => a - b)

    // spacing between the steps vertically and nodes horizontally
    const stepSpacing = 150
    const horizontalSpacing = 250

    const nodes = steps.flatMap((step, stepIndex) =>
      grouped[step].map((r, index) => ({
        id: r.id.toString(),
        data: {
          label: (
            <div className="text-center">
              <div className="fw-bold">Rule #: {r.external_id}</div>
              <div className="fw-bold">
                <AssignedBadge rule={r} />
              </div>
              <div className="text-muted small">Step {r.step_number}</div>
              <div className="text-muted small">
                Department: {r.department ? r.department : 'All'}
              </div>
              <div className="text-muted small">
                Amount: {r.lower_amount} - {r.upper_amount}
              </div>
              <div className="text-muted small">
                {rule.rule_type === 'A' ? (
                  <div className="text-muted small">
                    <FontAwesomeIcon icon={faCheckCircle} /> Approval:{' '}
                    {r.approval_type}
                  </div>
                ) : (
                  <div className="text-muted small">
                    <FontAwesomeIcon icon={faPaperPlane} /> Notification{' '}
                    {r.approval_type}
                  </div>
                )}
              </div>
            </div>
          )
        },
        position: { x: index * horizontalSpacing, y: stepIndex * stepSpacing },
        className: r.id === rule.id ? 'current-step' : undefined
      }))
    )

    const edges = []
    steps.forEach((step, stepIndex) => {
      if (stepIndex === steps.length - 1) return
      const nextStep = steps[stepIndex + 1]
      grouped[step].forEach((current) => {
        grouped[nextStep].forEach((next) => {
          edges.push({
            id: `${current.id}-${next.id}`,
            source: current.id.toString(),
            target: next.id.toString()
          })
        })
      })
    })

    return { nodes, edges }
  }, [data, isSuccess, rule])

  return (
    <div style={{ height: 600 }}>
      <ReactFlowProvider>
        <FlowContainer
          nodes={nodes}
          edges={edges}
          focusId={rule.id.toString()}
        />
      </ReactFlowProvider>
    </div>
  )
}

export default FlowchartTab

function FlowContainer({ nodes, edges, focusId }) {
  const { setCenter } = useReactFlow()

  useEffect(() => {
    const node = nodes.find((n) => n.id === focusId)
    if (node) {
      setCenter(node.position.x, node.position.y, { zoom: 1.2 })
    }
  }, [focusId, nodes, setCenter])

  return (
    <ReactFlow nodes={nodes} edges={edges} fitView>
      <Background />
      <Controls />
    </ReactFlow>
  )
}
